import { useCallback, useState } from 'react';
import { del, post } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { useResource } from '../../lib/useResource.js';
import type { RespostaRapida } from '../../lib/respostas.js';
import { Alert, Card, Empty, ErrorBox, Field, Modal, useSubmit, useToast } from '../../components/ui.js';

interface Edicao {
  id?: string;
  atalho: string;
  titulo: string;
  texto: string;
  ordem: number;
}

const VAZIA: Edicao = { atalho: '', titulo: '', texto: '', ordem: 0 };

/**
 * Mensagens prontas da equipe. No atendimento, digitar "/" na caixa de texto
 * abre esta lista. Quem configura a loja cadastra; todo mundo que atende usa.
 */
export function MensagensProntas() {
  const { can } = useSession();
  const podeEditar = can('company.settings.write');
  const toast = useToast();
  const [recarga, setRecarga] = useState(0);
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const lista = useResource<{ respostas: RespostaRapida[] }>('/api/respostas-rapidas', [recarga]);

  const salvar = useSubmit(
    useCallback(async () => {
      if (!edicao) return;
      await post('/api/respostas-rapidas', edicao);
      setEdicao(null);
      setRecarga((n) => n + 1);
      toast('Mensagem pronta salva.');
    }, [edicao, toast]),
  );

  async function remover(r: RespostaRapida) {
    if (!window.confirm(`Apagar a mensagem pronta /${r.atalho}?`)) return;
    await del(`/api/respostas-rapidas/${r.id}`);
    setRecarga((n) => n + 1);
    toast('Mensagem pronta apagada.');
  }

  return (
    <div className="stack">
      <Card
        title="Mensagens prontas"
        description={
          <>
            No atendimento, digite <strong>/</strong> na caixa de texto para escolher uma destas mensagens. Ela entra
            na caixa para você conferir e ajustar antes de enviar. No texto, <code>{'{atendente}'}</code> vira o seu
            nome e <code>{'{cliente}'}</code> o nome do cliente.
          </>
        }
        actions={
          podeEditar ? (
            <button type="button" className="primary" onClick={() => setEdicao({ ...VAZIA })}>
              Nova mensagem
            </button>
          ) : null
        }
      >
        <ErrorBox error={lista.error} />
        {lista.data && lista.data.respostas.length === 0 ? (
          <Empty title="Nenhuma mensagem pronta ainda." />
        ) : (
          <ul className="list">
            {(lista.data?.respostas ?? []).map((r) => (
              <li key={r.id}>
                <div className="list-main">
                  <div className="list-title">
                    /{r.atalho} · {r.titulo}
                  </div>
                  <div className="list-sub" style={{ whiteSpace: 'pre-wrap' }}>
                    {r.texto}
                  </div>
                </div>
                {podeEditar ? (
                  <div className="row" style={{ gap: 8 }}>
                    <button type="button" className="small" onClick={() => setEdicao({ ...r })}>
                      Editar
                    </button>
                    <button type="button" className="small danger" onClick={() => void remover(r)}>
                      Apagar
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {!podeEditar ? <Alert tone="info">Só o proprietário e o gerente cadastram e editam mensagens prontas.</Alert> : null}

      {edicao ? (
        <Modal
          title={edicao.id ? 'Editar mensagem pronta' : 'Nova mensagem pronta'}
          onClose={() => setEdicao(null)}
          footer={
            <>
              <button type="button" onClick={() => setEdicao(null)}>
                Cancelar
              </button>
              <button type="button" className="primary" disabled={salvar.saving} onClick={() => void salvar.submit()}>
                {salvar.saving ? 'Salvando…' : 'Salvar'}
              </button>
            </>
          }
        >
          <div className="stack">
            <Field label="Atalho" hint='O que se digita depois da barra. Ex.: "orcamento" vira /orcamento.' required>
              <input value={edicao.atalho} onChange={(e) => setEdicao({ ...edicao, atalho: e.target.value })} />
            </Field>
            <Field label="Título" required>
              <input value={edicao.titulo} onChange={(e) => setEdicao({ ...edicao, titulo: e.target.value })} />
            </Field>
            <Field label="Texto" required>
              <textarea
                rows={10}
                value={edicao.texto}
                onChange={(e) => setEdicao({ ...edicao, texto: e.target.value })}
              />
            </Field>
            <Field label="Ordem na lista" hint="Menor aparece primeiro.">
              <input
                type="number"
                min={0}
                value={edicao.ordem}
                onChange={(e) => setEdicao({ ...edicao, ordem: Number(e.target.value) || 0 })}
              />
            </Field>
            <ErrorBox error={salvar.error} />
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
